
import React, { useState, useCallback } from "react";
import {
  SuccessMessage,
  FailMessage,
  Loading,
  FormatValueToDecimal,
  CompanySelect,
  FormatDateToWord,
  VendorSelect,
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from "axios";
import ExcelJS from "exceljs";
import { PDFDocument } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';

export default function AccountsPayableAging() {
  //#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
  //#endregion

  //#region Variables
  const [company, setCompany] = useState(null);

  // Default dates: beginning of current year to today
  const today = new Date();
  const localDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const [dateFrom, setDateFrom] = useState(
    `${today.getFullYear()}-01-01`
  );
  const [dateTo, setDateTo] = useState(localDate(today));
  //#endregion


  


  //#region Vendor 
    const [vendor, setVendor] = useState(null);
    const [showVendorSelect, setShowVendorSelect] = useState(false);
  
    const openVendorSelect = () => {
      setShowVendorSelect(true)
    }
    const closeVendorSelect = () => {
      setShowVendorSelect(false)
    }
  //#endregion
  




  //#region Generate

  const fetchReportData = useCallback(async () => {
    const body = {
      vendorId: vendor ? vendor?.vendorId : null,
      dateFrom,
      dateTo,
    };

    try {
      const response = await axios.post(
        `${apiUrl}/api/Report/report-data/accounts-payable-aging`,
        body,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching Report:", error);
      setFailMessage(
        error.response?.data?.message ||
        error.response?.data ||
        "Error fetching Report."
      );
      return null;
    }
  }, [dateFrom, dateTo, vendor]);

  const validateFilters = () => {
    if (!dateFrom || !dateTo || !company) {
      setFailMessage("Please fill up all fields.");
      return false;
    }

    if (new Date(dateFrom) > new Date(dateTo)) {
      setFailMessage("Date From cannot be later than Date To.");
      return false;
    }

    return true;
  };

  //#region PDF

  const generateReportPdf = async () => {
    if (!validateFilters()) return;

    setLoading(true);

    try {
      const responseData = await fetchReportData();

      if (!responseData) {
        setFailMessage("No record found.");
        return;
      }

      // Support both camelCase and PascalCase backend responses
      const records =
        responseData.details ||
        responseData.Details ||
        (Array.isArray(responseData) ? responseData : []);

      if (records.length === 0) {
        setFailMessage("No aging records found for the selected period.");
        return;
      }

      const pdfUrl = `${window.location.origin}/docs/ACCOUNTS_PAYABLE_AGING_REPORT.pdf`;

      const templateBytes = await fetch(pdfUrl).then((r) => {
        if (!r.ok) throw new Error("Report PDF template not found.");
        return r.arrayBuffer();
      });

      const templateDoc = await PDFDocument.load(templateBytes);
      const pdfDoc = await PDFDocument.load(templateBytes);

      pdfDoc.registerFontkit(fontkit);

      const fontResponse = await fetch("/calibri.ttf");
      const fontBytes = await fontResponse.arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);

      const boldResponse = await fetch("/calibrib.ttf");
      const boldBytes = await boldResponse.arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);

      // Helpers
      const truncateText = (text, maxWidth, font, fontSize) => {
        if (!text) return "";
        let truncated = String(text);
        const ellipsis = "...";
        while (
          truncated.length > 0 &&
          font.widthOfTextAtSize(truncated + ellipsis, fontSize) > maxWidth
        ) {
          truncated = truncated.slice(0, -1);
        }
        return truncated.length < String(text).length
          ? truncated + ellipsis
          : String(text);
      };

      // Helper for right-aligned numbers
      const drawTextRight = (page, text, rightX, y, font, size) => {
        const width = font.widthOfTextAtSize(text, size);
        page.drawText(text, {
          x: rightX - width,
          y,
          size,
          font,
        });
      };

      const drawHeader = (page) => {
        // Company Info
        page.drawText(company.name?.toString() ?? "", {
          x: 35,
          y: 583,
          size: 16,
          font: calibriBold,
        });

        page.drawText(company.address?.toString() ?? "", {
          x: 35,
          y: 573,
          size: 8,
          font: calibriFont,
        });

        page.drawText(company.tin ? `TIN: ${company.tin}` : "", {
          x: 35,
          y: 563,
          size: 8,
          font: calibriFont,
        });

        // Filter Period
        page.drawText(
          `${FormatDateToWord(new Date(`${dateFrom}T00:00:00`))} to ${FormatDateToWord(new Date(`${dateTo}T00:00:00`))}`,
          {
            x: 85,
            y: 506,
            size: 8,
            font: calibriFont,
          }
        );

        page.drawText(FormatDateToWord(new Date()), {
          x: 85,
          y: 495,
          size: 8,
          font: calibriFont,
        });
      };

      let currentPage = pdfDoc.getPages()[0];
      drawHeader(currentPage);

      let yPosition = 446;
      const bottomThreshold = 80;
      const lineHeight = 14;

      const addNewPage = async () => {
        const [templatePage] = await pdfDoc.copyPages(templateDoc, [0]);
        currentPage = pdfDoc.addPage(templatePage);
        drawHeader(currentPage);
        yPosition = 446;
      };

      // Column Right X Boundaries for clean accounting alignment
      const colRightCurrent = 340;
      const colRight31to60 = 435;
      const colRight61to90 = 545;
      const colRightOver90 = 645;
      const colRightTotal = 755;

      let sumCurrent = 0;
      let sum31to60 = 0;
      let sum61to90 = 0;
      let sumOver90 = 0;
      let sumTotal = 0;

      for (const record of records) {
        if (yPosition - lineHeight < bottomThreshold) {
          await addNewPage();
        }

        const vendorName = truncateText(record.vendorName ?? "Unknown Vendor", 180, calibriFont, 8);
        const current = Number(record.current || 0);
        const days31To60 = Number(record.days31To60 || 0);
        const days61To90 = Number(record.days61To90 || 0);
        const over90Days = Number(record.over90Days || 0);
        const totalOutstanding = Number(record.totalOutstanding || 0);

        // Vendor Name (Left aligned)
        currentPage.drawText(vendorName, {
          x: 40,
          y: yPosition,
          size: 8,
          font: calibriFont,
        });

        // Aging Columns (Right aligned)
        drawTextRight(currentPage, `${FormatValueToDecimal(current)}`, colRightCurrent, yPosition, calibriFont, 8);
        drawTextRight(currentPage, `${FormatValueToDecimal(days31To60)}`, colRight31to60, yPosition, calibriFont, 8);
        drawTextRight(currentPage, `${FormatValueToDecimal(days61To90)}`, colRight61to90, yPosition, calibriFont, 8);
        drawTextRight(currentPage, `${FormatValueToDecimal(over90Days)}`, colRightOver90, yPosition, calibriFont, 8);
        drawTextRight(currentPage, `${FormatValueToDecimal(totalOutstanding)}`, colRightTotal, yPosition, calibriFont, 8);

        // Accumulate Totals
        sumCurrent += current;
        sum31to60 += days31To60;
        sum61to90 += days61To90;
        sumOver90 += over90Days;
        sumTotal += totalOutstanding;

        yPosition -= lineHeight;
      }

      // Grand Total Section
      if (yPosition - 30 < bottomThreshold) {
        await addNewPage();
      }

      // Top border of summary
      currentPage.drawLine({
        start: { x: 35, y: yPosition },
        end: { x: 755, y: yPosition },
        thickness: 0.8,
      });

      yPosition -= 14;

      currentPage.drawText("GRAND TOTAL", {
        x: 35,
        y: yPosition,
        size: 8,
        font: calibriBold,
      });

      // Grand Total Numbers (Right aligned)
      drawTextRight(currentPage, `${FormatValueToDecimal(responseData.totalCurrent ?? sumCurrent)}`, colRightCurrent, yPosition, calibriBold, 8);
      drawTextRight(currentPage, `${FormatValueToDecimal(responseData.total31To60 ?? sum31to60)}`, colRight31to60, yPosition, calibriBold, 8);
      drawTextRight(currentPage, `${FormatValueToDecimal(responseData.total61To90 ?? sum61to90)}`, colRight61to90, yPosition, calibriBold, 8);
      drawTextRight(currentPage, `${FormatValueToDecimal(responseData.totalOver90Days ?? sumOver90)}`, colRightOver90, yPosition, calibriBold, 8);
      drawTextRight(currentPage, `${FormatValueToDecimal(responseData.grandTotalOutstanding ?? sumTotal)}`, colRightTotal, yPosition, calibriBold, 8);

      // Double bottom accounting underline
      currentPage.drawLine({
        start: { x: 35, y: yPosition - 4 },
        end: { x: 755, y: yPosition - 4 },
        thickness: 0.5,
      });
      currentPage.drawLine({
        start: { x: 35, y: yPosition - 6.5 },
        end: { x: 755, y: yPosition - 6.5 },
        thickness: 0.5,
      });

      // Page Numbering
      const allPages = pdfDoc.getPages();
      const totalPages = allPages.length;
      allPages.forEach((page, index) => {
        page.drawText(`Page ${index + 1} of ${totalPages}`, {
          x: 680,
          y: 580,
          size: 9,
          font: calibriBold,
        });
      });

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `ACCOUNTS_PAYABLE_AGING_${dateFrom}_${dateTo}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Report PDF Error:", error);
      setFailMessage(error.message || "Error processing Aging PDF.");
    } finally {
      setLoading(false);
    }
  };

  //#endregion

  //#region Excel

  const exportExcel = async () => {
    if (!validateFilters()) return;

    setLoading(true);

    try {
      const responseData = await fetchReportData();

      if (!responseData) {
        setFailMessage("No record found.");
        return;
      }

      const records =
        responseData.details ||
        responseData.Details ||
        (Array.isArray(responseData) ? responseData : []);

      if (records.length === 0) {
        setFailMessage("No aging records found for the selected period.");
        return;
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("AR Aging Report");

      // 1. Company Header
      worksheet.addRow([company.name?.toUpperCase() ?? ""]).font = {
        bold: true,
        size: 14,
      };
      worksheet.addRow([company.address ?? ""]);
      worksheet.addRow([company.tin ? `TIN: ${company.tin}` : ""]);
      worksheet.addRow([]);

      // 2. Report Header
      worksheet.addRow(["ACCOUNTS PAYABLE AGING REPORT"]).font = {
        bold: true,
        size: 12,
      };
      worksheet.addRow([
        `Period: ${FormatDateToWord(new Date(`${dateFrom}T00:00:00`))} to ${FormatDateToWord(new Date(`${dateTo}T00:00:00`))}`,
      ]);
      worksheet.addRow([`Date Printed: ${FormatDateToWord(new Date())}`]);
      worksheet.addRow([]);

      // 3. Table Column Headers
      const headerRow = worksheet.addRow([
        "Vendor",
        "Current (0–30)",
        "31–60 Days",
        "61–90 Days",
        "Over 90 Days",
        "Total Outstanding",
      ]);

      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "1E40AF" }, // Dark Navy Blue
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      // 4. Data Rows
      let sumCurrent = 0;
      let sum31to60 = 0;
      let sum61to90 = 0;
      let sumOver90 = 0;
      let sumTotal = 0;

      records.forEach((record) => {
        const current = Number(record.current || 0);
        const days31To60 = Number(record.days31To60 || 0);
        const days61To90 = Number(record.days61To90 || 0);
        const over90Days = Number(record.over90Days || 0);
        const totalOutstanding = Number(record.totalOutstanding || 0);

        sumCurrent += current;
        sum31to60 += days31To60;
        sum61to90 += days61To90;
        sumOver90 += over90Days;
        sumTotal += totalOutstanding;

        const row = worksheet.addRow([
          record.vendorName ?? "Unknown Vendor",
          current,
          days31To60,
          days61To90,
          over90Days,
          totalOutstanding,
        ]);

        // Apply currency format and right alignment to numeric columns (2 to 6)
        for (let col = 2; col <= 6; col++) {
          const cell = row.getCell(col);
          cell.numFmt = "#,##0.00";
          cell.alignment = { horizontal: "right" };
        }
      });

      // 5. Grand Total Row
      const totalRow = worksheet.addRow([
        "GRAND TOTAL",
        responseData.totalCurrent ?? sumCurrent,
        responseData.total31To60 ?? sum31to60,
        responseData.total61To90 ?? sum61to90,
        responseData.totalOver90Days ?? sumOver90,
        responseData.grandTotalOutstanding ?? sumTotal,
      ]);

      totalRow.font = { bold: true };

      for (let col = 1; col <= 6; col++) {
        const cell = totalRow.getCell(col);
        if (col > 1) {
          cell.numFmt = "#,##0.00";
          cell.alignment = { horizontal: "right" };
        }
        cell.border = {
          top: { style: "thin" },
          bottom: { style: "double" }, // Standard accounting double underline
        };
      }

      // 6. Responsive Column Widths
      worksheet.columns = [
        { width: 35 }, // Vendor
        { width: 18 }, // Current (0-30)
        { width: 18 }, // 31-60 Days
        { width: 18 }, // 61-90 Days
        { width: 18 }, // Over 90 Days
        { width: 22 }, // Total Outstanding
      ];

      // 7. Download
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `Accounts_Payable_Aging_${dateFrom}_to_${dateTo}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Report Excel Error:", error);
      setFailMessage("Error generating Excel file.");
    } finally {
      setLoading(false);
    }
  };

  //#endregion

  //#endregion

  //#region Access Template

  const accountData = JSON.parse(localStorage.getItem("accountData"));

  if (!accountData?.accessTemplate?.hasAccountsPayableAging) {
    return <NoAccess />;
  }

  //#endregion

  //#region UI

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon
                fontSize="large"
                className="text-theme-color"
              />
              Accounts Payable Aging Report
            </h2>

            <p className="text-gray-500 mt-1">
              Vendor outstanding balances by aging bucket.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-semibold mb-1 text-gray-700">
              Vendor
            </label>
            <div className="flex flex-row items-center">
              <input
                type="text"
                placeholder="Select Vendor"
                className="w-full border border-gray-300 text-sm rounded-l-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color transition"
                value={`${vendor ? `${vendor?.vendorId} - ${vendor?.name1}` : ""}`}
                readOnly
              />
              {!vendor ?
                <button className="bg-theme-color hover:bg-theme-color-h p-[5px] rounded-r-lg" onClick={openVendorSelect}>
                  <PersonSearchIcon className="text-white"/>
                </button> 
                :
                <button className="bg-red-400 hover:bg-red-500 p-[5px] rounded-r-lg" onClick={()=>setVendor(null)}>
                  <RemoveCircleIcon className="text-white"/>
                </button>
              }
            </div>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Company <span className="text-red-500">*</span>
            </label>

            <CompanySelect
              data={company}
              setData={setCompany}
              isEditable={true}
            />
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Date From <span className="text-red-500">*</span>
            </label>

            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700">
              Date To <span className="text-red-500">*</span>
            </label>

            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

        </div>

        <div className="mt-5 p-6 bg-white rounded-2xl shadow-sm border border-gray-100">
          <h2 className="text-lg font-bold text-gray-800 mb-6 px-2">
            Generate Reports
          </h2>

          <div className="hidden md:grid grid-cols-12 gap-4 px-4 mb-2 text-xs font-black uppercase tracking-widest text-gray-400">
            <div className="col-span-2">Report Category</div>
            <div className="col-span-5">PDF Reports</div>
            <div className="col-span-5 border-l border-gray-100">
              Excel Reports
            </div>
          </div>

          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-12 items-center gap-4 p-4 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors">
              <div className="col-span-2">
                <span className="text-sm font-bold text-gray-700">
                  Summary
                </span>
              </div>

              <div className="col-span-5 flex flex-wrap gap-2">
                <button
                  onClick={generateReportPdf}
                  className="flex items-center gap-2 bg-white text-gray-700 border border-gray-300 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-gray-50 transition-all active:scale-95"
                >
                  PDF Summary
                </button>
              </div>

              <div className="col-span-5 flex flex-wrap gap-2 border-t md:border-t-0 md:border-l border-gray-100 pt-3 md:pt-0">
                <button
                  onClick={exportExcel}
                  className="flex items-center gap-2 bg-green-50 text-green-700 border border-green-200 px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-green-100 transition-all active:scale-95"
                >
                  Excel Summary
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />

        <FailMessage
          message={failMessage}
          onClose={() => setFailMessage("")}
        />
      </div>

      <VendorSelect 
        visible={showVendorSelect} 
        onClose={closeVendorSelect}
        modalName={"Select Vendor"}
        data={vendor} setData={setVendor}
      />

      {loading && <Loading />}
    </>
  );

  //#endregion
}
